import React, { useState, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import Select from "react-select";
import "../style/Tendencias.css";

// Funciones auxiliares para parsear y formatear "YYYY-MM"
function parseYearMonth(str) {
  const [year, month] = str.split("-").map(Number);
  return { year, month };
}

// Para ordenar un array de cadenas "YYYY-MM"
function compareYearMonth(a, b) {
  const { year: ya, month: ma } = parseYearMonth(a);
  const { year: yb, month: mb } = parseYearMonth(b);

  if (ya !== yb) return ya - yb;
  return ma - mb;
}

// Obtener el mes anterior (ej: 2025-01 => 2024-12)
function getPreviousMonth(year, month) {
  if (month === 1) {
    return { year: year - 1, month: 12 };
  }
  return { year, month: month - 1 };
}

// Formatear un objeto {year, month} a "YYYY-MM"
function formatYearMonth(year, month) {
  return `${year.toString().padStart(4, "0")}-${month
    .toString()
    .padStart(2, "0")}`;
}

const Tendencias = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [countries, setCountries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [chartOptions, setChartOptions] = useState(null);

  // Guardar mes más reciente de TODO el JSON y su mes anterior
  const [latestMonth, setLatestMonth] = useState(null);
  const [previousMonth, setPreviousMonth] = useState(null);

  // 1. Cargar datos y determinar el mes más reciente del JSON
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          "https://marketbusters.pro/chart/graficos/proxy_tendencias.php"
        );
        const json = await response.json();
        setData(json);

        // Construir lista de países
        const uniqueCountries = [...new Set(json.map((item) => item.pais))];
        setCountries(
          uniqueCountries.map((country) => ({
            value: country,
            label: country,
          }))
        );

        // Seleccionar el primer país por defecto
        if (uniqueCountries.length > 0) {
          setSelectedCountry({
            value: uniqueCountries[0],
            label: uniqueCountries[0],
          });
        }

        // ====== OBTENER MES MÁS RECIENTE DE TODO EL JSON ======
        const allMonths = [];
        json.forEach((item) => {
          if (Array.isArray(item.variaciones)) {
            item.variaciones.forEach(({ mes }) => {
              // Aseguramos que tenga formato "YYYY-MM"
              if (typeof mes === "string" && mes.includes("-")) {
                allMonths.push(mes);
              }
            });
          }
        });

        // Encontrar el máximo mes (más reciente)
        // Comparando año y mes con compareYearMonth
        let maxMonth = null;
        allMonths.forEach((m) => {
          if (!maxMonth) {
            maxMonth = m; // primero
          } else {
            if (compareYearMonth(m, maxMonth) > 0) {
              maxMonth = m;
            }
          }
        });

        // Calcular el mes anterior
        if (maxMonth) {
          const { year, month } = parseYearMonth(maxMonth);
          const prev = getPreviousMonth(year, month);
          setLatestMonth(maxMonth);
          setPreviousMonth(formatYearMonth(prev.year, prev.month));
        }
      } catch (error) {
        console.error("Error al obtener los datos:", error);
      }
    };

    fetchData();
  }, []);

  // 2. Filtrar datos por país y determinar las categorías
  //    usando la suma de los últimos 2 meses (previousMonth, latestMonth).
  useEffect(() => {
    // Necesitamos que estén definidos los meses
    if (!latestMonth || !previousMonth) return;
    if (!selectedCountry) return;

    // Filtrar datos por país
    const filtered = data.filter(
      (item) => item.pais === selectedCountry.value
    );
    setFilteredData(filtered);

    // Para rankear categorías, usamos SOLO los últimos 2 meses
    const validMonths = [previousMonth, latestMonth];

    const categoryTotals = filtered.reduce((acc, item) => {
      if (!Array.isArray(item.variaciones)) return acc;

      // Suma de los últimos 2 meses para esta supracategory
      const recentSum = item.variaciones
        .filter(({ mes }) => mes && validMonths.includes(mes))
        .reduce((sum, { variacion }) => {
          return sum + Math.abs(parseFloat(variacion || 0));
        }, 0);

      // Acumular en supracategory
      if (recentSum > 0) {
        acc[item.supracategory] = (acc[item.supracategory] || 0) + recentSum;
      }
      return acc;
    }, {});

    // Ordenar por valor descendente
    const sortedCategories = Object.entries(categoryTotals)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 30) // top 30, si quieres
      .map(([key]) => ({ value: key, label: key }));

    setCategories(sortedCategories);

    // Seleccionar la primera categoría por defecto
    if (sortedCategories.length > 0) {
      setSelectedCategory(sortedCategories[0]);
    }
  }, [selectedCountry, data, latestMonth, previousMonth]);

  // 3. Construir las opciones del gráfico con TODO el historial de la categoría
  useEffect(() => {
    if (!selectedCategory) return;
    if (filteredData.length === 0) return;

    // Filtrar data por la categoría elegida
    const filteredByCategory = filteredData.filter(
      (item) => item.supracategory === selectedCategory.value
    );

    // Agrupar TODAS las variaciones de la categoría (no solo 2 meses)
    const groupedData = {};
    filteredByCategory.forEach((item) => {
      if (!Array.isArray(item.variaciones)) return;
      item.variaciones.forEach(({ mes, variacion }) => {
        // Omitimos si mes no tiene formato "YYYY-MM"
        if (!mes || !mes.includes("-")) return;

        const valor = Math.abs(parseFloat(variacion || 0));
        if (!groupedData[mes]) {
          groupedData[mes] = 0;
        }
        groupedData[mes] += valor;
      });
    });

    // Ordenar todas las fechas (meses)
    const allMeses = Object.keys(groupedData).sort(compareYearMonth);
    const valores = allMeses.map((m) => groupedData[m]);

    // Construir opciones de ECharts
    const options = {
      backgroundColor: "#2b2b2b",
      title: {
        text: `Tendencias: ${selectedCountry.label} - ${selectedCategory.label}`,
        left: "center",
        textStyle: { color: "#ffffff" },
      },
      tooltip: { trigger: "axis" },
      legend: { show: false },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: allMeses,
        axisLine: { lineStyle: { color: "#ffffff" } },
        axisLabel: { color: "#ffffff" },
      },
      yAxis: {
        type: "value",
        axisLine: { lineStyle: { color: "#ffffff" } },
        axisLabel: { color: "#ffffff" },
        splitLine: { lineStyle: { color: "#555555" } },
      },
      series: [
        {
          name: "Variación Absoluta",
          type: "line",
          smooth: true,
          data: valores,
          lineStyle: { width: 2 },
          areaStyle: { color: "rgba(84, 112, 198, 0.2)" },
        },
      ],
    };

    setChartOptions(options);
  }, [filteredData, selectedCategory, selectedCountry]);

  return (
    <div className="tendencias-container">
      <div className="tendencias-filters">
        <Select
          options={countries}
          value={selectedCountry}
          onChange={setSelectedCountry}
          placeholder="Selecciona un país"
          className="filter-select"
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            control: (base) => ({
              ...base,
              backgroundColor: "#404040",
              border: "1px solid #606060",
              color: "#ffffff",
            }),
            singleValue: (base) => ({
              ...base,
              color: "#ffffff",
            }),
            menu: (base) => ({
              ...base,
              backgroundColor: "#2b2b2b",
              zIndex: 9999,
            }),
            option: (base, state) => ({
              ...base,
              backgroundColor: state.isFocused ? "#606060" : "#2b2b2b",
              color: "#ffffff",
              cursor: "pointer",
            }),
          }}
        />
        <Select
          options={categories}
          value={selectedCategory}
          onChange={setSelectedCategory}
          placeholder="Selecciona una categoría"
          isDisabled={!categories.length}
          className="filter-select"
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            control: (base) => ({
              ...base,
              backgroundColor: "#404040",
              border: "1px solid #606060",
              color: "#ffffff",
            }),
            singleValue: (base) => ({
              ...base,
              color: "#ffffff",
            }),
            menu: (base) => ({
              ...base,
              backgroundColor: "#2b2b2b",
              zIndex: 9999,
            }),
            option: (base, state) => ({
              ...base,
              backgroundColor: state.isFocused ? "#606060" : "#2b2b2b",
              color: "#ffffff",
              cursor: "pointer",
            }),
          }}
        />
      </div>
      <div className="tendencias-chart">
        {chartOptions ? (
          <ReactECharts
            option={chartOptions}
            style={{ height: "500px", width: "100%" }}
          />
        ) : (
          <p className="grafico-title">Cargando datos...</p>
        )}
      </div>
    </div>
  );
};

export default Tendencias;
